// Copyright (c) Ping. All rights reserved.

import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { Modal as PingModal } from '../../components/Modal';
import { createTheme } from '../../design-system/theme';
import { tasksStrings } from './strings';
import type { TaskItem, TaskPriority } from './types';

export interface EditTaskPatch {
  readonly title: string;
  readonly priority: TaskPriority;
}

export interface EditTaskModalProps {
  readonly visible: boolean;
  readonly task: TaskItem | null;
  readonly onClose: () => void;
  readonly onSubmit: (taskId: TaskItem['id'], patch: EditTaskPatch) => void;
}

const PRIORITY_ORDER: readonly TaskPriority[] = ['low', 'medium', 'high', 'urgent'];

/** Modal that pre-populates from an existing task and emits a partial update. */
export const EditTaskModal: React.FC<EditTaskModalProps> = ({
  visible,
  task,
  onClose,
  onSubmit,
}) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');

  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('medium');

  useEffect(() => {
    if (task !== null) {
      setTitle(task.title);
      setPriority(task.priority);
    }
  }, [task]);

  const handleSubmit = useCallback(() => {
    if (task === null) {
      return;
    }
    const trimmed = title.trim();
    if (trimmed.length === 0) {
      return;
    }
    onSubmit(task.id, { title: trimmed, priority });
    onClose();
  }, [task, title, priority, onSubmit, onClose]);

  return (
    <PingModal visible={visible} onClose={onClose} accessibilityLabel={tasksStrings.editTaskTitle}>
      <View>
        <Text style={[styles.title, { color: theme.colors.text.primary }]}>
          {tasksStrings.editTaskTitle}
        </Text>
        <Text style={[styles.label, { color: theme.colors.text.secondary }]}>
          {tasksStrings.taskTitleLabel}
        </Text>
        <TextInput
          value={title}
          onChangeText={setTitle}
          accessibilityLabel={tasksStrings.taskTitleLabel}
          style={[
            styles.input,
            {
              color: theme.colors.text.primary,
              borderColor: theme.colors.border.primary,
              backgroundColor: theme.colors.surface.secondary,
            },
          ]}
        />
        <Text style={[styles.label, { color: theme.colors.text.secondary }]}>
          {tasksStrings.priorityLabel}
        </Text>
        <View style={styles.priorityRow}>
          {PRIORITY_ORDER.map((p) => {
            const selected = p === priority;
            return (
              <Pressable
                key={p}
                onPress={() => setPriority(p)}
                accessibilityRole="button"
                accessibilityLabel={tasksStrings.priority(p)}
                accessibilityState={{ selected }}
                style={({ pressed }) => [
                  styles.priorityChip,
                  {
                    borderColor: selected
                      ? theme.colors.accent.indigo
                      : theme.colors.border.secondary,
                    backgroundColor: selected ? `${theme.colors.accent.indigo}1A` : 'transparent',
                    opacity: pressed ? 0.7 : 1,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.priorityChipText,
                    {
                      color: selected ? theme.colors.accent.indigo : theme.colors.text.primary,
                    },
                  ]}
                >
                  {tasksStrings.priority(p)}
                </Text>
              </Pressable>
            );
          })}
        </View>
        <View style={styles.actions}>
          <Pressable
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel={tasksStrings.cancelCta}
            style={({ pressed }) => [styles.actionButton, pressed && { opacity: 0.7 }]}
          >
            <Text style={[styles.cancelText, { color: theme.colors.text.secondary }]}>
              {tasksStrings.cancelCta}
            </Text>
          </Pressable>
          <Pressable
            onPress={handleSubmit}
            accessibilityRole="button"
            accessibilityLabel={tasksStrings.saveCta}
            accessibilityState={{ disabled: title.trim().length === 0 }}
            disabled={title.trim().length === 0}
            style={({ pressed }) => [
              styles.actionButton,
              styles.primaryButton,
              { backgroundColor: theme.colors.accent.indigo },
              pressed && { opacity: 0.85 },
              title.trim().length === 0 && { opacity: 0.4 },
            ]}
          >
            <Text style={styles.primaryText}>{tasksStrings.saveCta}</Text>
          </Pressable>
        </View>
      </View>
    </PingModal>
  );
};

const styles = StyleSheet.create({
  title: {
    fontSize: 17,
    fontWeight: '700',
    marginBottom: 12,
  } satisfies TextStyle,
  label: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 6,
    marginTop: 12,
    textTransform: 'uppercase',
  } satisfies TextStyle,
  input: {
    fontSize: 14,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
  } satisfies TextStyle,
  priorityRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  } satisfies ViewStyle,
  priorityChip: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 12,
    borderWidth: 1,
    marginRight: 6,
    marginBottom: 6,
  } satisfies ViewStyle,
  priorityChipText: {
    fontSize: 12,
    fontWeight: '600',
  } satisfies TextStyle,
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 20,
  } satisfies ViewStyle,
  actionButton: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 8,
    marginLeft: 8,
  } satisfies ViewStyle,
  primaryButton: {
    minWidth: 80,
    alignItems: 'center',
  } satisfies ViewStyle,
  cancelText: {
    fontSize: 14,
    fontWeight: '600',
  } satisfies TextStyle,
  primaryText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#FFFFFF',
  } satisfies TextStyle,
});
